import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { apiBaseUrl } from '../lib/env';
import { refreshSession } from './refresh';

// `credentials: 'include'` is required so the v5_auth cookie is sent on
// every call. With same-eTLD hosting the cookie's SameSite=Lax handles
// top-level navigations; cross-site XHR needs the explicit credentials flag.
//
// CSRF: the API uses a double-submit cookie. On unsafe (POST/PUT/PATCH/
// DELETE) requests we read the `v5_csrf` cookie and echo it back in the
// X-CSRF-Token header. The middleware below does that transparently for
// every typed call (api.POST/etc). Without this every write returns 403.
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';');
  for (const raw of parts) {
    const c = raw.trim();
    if (c.startsWith(prefix)) {
      return decodeURIComponent(c.slice(prefix.length));
    }
  }
  return null;
}

const csrfMiddleware: Middleware = {
  async onRequest({ request }) {
    if (SAFE_METHODS.has(request.method)) return undefined;
    const token = readCookie('v5_csrf');
    if (token) request.headers.set('X-CSRF-Token', token);
    return request;
  },
};

// On 401 from any non-auth endpoint, try refreshSession() once and
// replay the request. This catches the case where the access cookie
// expired in the background (e.g. laptop slept) but the refresh cookie
// is still valid — the user shouldn't have to re-login.
//
// The replay has to reissue the EXACT original request — including its
// body and the X-CSRF-Token header. By the time onResponse fires, the
// live `request` has had its body stream consumed by the first send, so
// cloning it then yields an empty (and, on a raw fetch, CSRF-less)
// request — writes would silently go out blank and re-fail with 403.
// Instead we clone the request in onRequest, BEFORE the body is read and
// AFTER csrfMiddleware has set the header (it runs first), and stash the
// clone keyed by the per-request `id` that openapi-fetch passes to both
// hooks. The replay then has the body, the CSRF header, and the base
// `X-Requested-With` header intact; we only re-assert credentials so the
// auth cookie is sent.
const pendingReplays = new Map<string, Request>();

const refreshOn401Middleware: Middleware = {
  onRequest({ request, id }) {
    if (SAFE_METHODS.has(request.method)) return undefined;
    // Snapshot the request while its body is still unread, so a 401 can
    // replay it faithfully. Cleared in onResponse (every path) so the map
    // never leaks.
    pendingReplays.set(id, request.clone());
    return undefined;
  },
  async onResponse({ request, response, id }) {
    const snapshot = pendingReplays.get(id);
    pendingReplays.delete(id);
    if (response.status !== 401) return undefined;
    const path = new URL(request.url).pathname;
    if (path.startsWith('/v5/auth/')) return undefined; // don't recurse on /me, /refresh, /login
    const refreshed = await refreshSession();
    if (!refreshed) {
      // Refresh failed → the session is truly gone. Notify listeners so
      // the SPA can drop the cached session + redirect to login instead
      // of letting the original 401 bubble up as a generic error-boundary
      // "Something went wrong! Failed to fetch" message.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('haket.session-expired'));
      }
      return undefined;
    }
    // Replay the pre-consumption clone (body + CSRF header preserved). For
    // safe GET/HEAD/OPTIONS there's no snapshot — the live request body is
    // empty anyway, so cloning it here is fine.
    const replay = snapshot ?? request.clone();
    return fetch(replay, { credentials: 'include' });
  },
  onError({ id }) {
    // A thrown fetch (network failure) skips onResponse, so release the
    // snapshot here too — otherwise the map leaks one clone per failed write.
    pendingReplays.delete(id);
    return undefined;
  },
};

// Normalises EMPTY response bodies that openapi-fetch would otherwise mishandle
// by calling response.json() on them. Two cases (see the body for detail):
//   • a SUCCESS with no content (202 Accepted from POST /ai-review, etc.) →
//     openapi-fetch's .json() on "" throws "Unexpected end of JSON input",
//     surfacing as a scary "your last change may not have been saved" toast.
//     We hand it a synthetic `null` body so the parse yields { data: null }.
//   • a bodyless non-2xx (rate-limited 429, bodyless 403/404) → openapi-fetch
//     resolves { error: undefined } so `if (error) throw error` treated it as
//     SUCCESS. We synthesize a JSON string so `error` is ALWAYS truthy. Hooks
//     that branch on response.status (404→null, 409→conflict, 429→rateLimited,
//     503→disabled, 401→signed-out) read status BEFORE the error check and are
//     unaffected.
//
// Registered FIRST: openapi-fetch runs onResponse in reverse registration
// order, so this sees the FINAL response — after the 401-refresh replay.
// 204/205/304 are body-less by spec (openapi-fetch won't parse them); skip them.
const bodylessErrorMiddleware: Middleware = {
  async onResponse({ response }) {
    // 204/205/304 are body-less by spec — openapi-fetch already knows not to
    // parse them, so leave them alone.
    if (response.status === 204 || response.status === 205 || response.status === 304) {
      return undefined;
    }

    const text = await response.clone().text();
    if (text.trim().length > 0) return undefined; // real body → parsed as usual

    // EMPTY body. Two cases, both of which openapi-fetch would otherwise choke
    // on by calling response.json() → "Unexpected end of JSON input":
    //  • A SUCCESS with no content — e.g. POST /ai-review returns 202 Accepted
    //    with only a Location header (it enqueues an async job). Give it a
    //    synthetic `null` JSON body so the parse yields { data: null } and the
    //    hook's `if (error)` stays false. (This was the "Failed to execute
    //    'json' on 'Response'" toast when starting an on-demand review.)
    //  • A non-2xx with no body — e.g. a rate-limited 429 or a bodyless 403/404
    //    that the limiter/handler wrote no body for. Synthesize a JSON string
    //    so `error` is ALWAYS truthy and the ubiquitous `if (error) throw error`
    //    fires instead of treating it as success. Hooks that branch on
    //    response.status read the status BEFORE the error check, so they're
    //    unaffected.
    const body = response.ok ? 'null' : JSON.stringify(`request failed (${response.status})`);
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: { 'content-type': 'application/json' },
    });
  },
};;

export const api = createClient<paths>({
  baseUrl: apiBaseUrl,
  credentials: 'include',
  headers: { 'X-Requested-With': 'haket-v5-ui' },
});

api.use(bodylessErrorMiddleware);
api.use(csrfMiddleware);
api.use(refreshOn401Middleware);
